// Bildschirm "Stichwortkonflikte" -- meine offenen Datenqualitaetsfaelle und das
// Schliessen (ST-TAX-002, Folgeschnitt B10; ADR-0059 Ziffer 9, D5).
//
// NUR LISTE UND SCHLIESSEN, keine Steward-Queue (CM-CAP-007 bleibt draussen);
// erreichbar ueber die Navigation (Gruppe Datenpflege). Die Liste hat KEINE Hauptaktion: jeder Fall traegt
// zwei Zweitaktionen ("Behoben", "Ist so gewollt"); der Weg zur Abhilfe ist der
// Link auf den Kontakt, an dem das ueberzaehlige Stichwort entfernt wird.
//
// Nach jedem Schliessen laedt die Liste neu und der Fokus geht in die
// Statusregion des Abschnitts (ux_principles ST-TAX-002, Muster Wiedervorlagen).

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { CLOSE_UNCERTAIN_MESSAGE, closeIssue, fetchOwnIssues, issuesSummary, issueSentence, resolveBlockedReason, UNREACHABLE_MESSAGE, type DataQualityIssueView } from '../data-quality.ts';
import { partyHref } from '../parties.ts';
import { AppLink } from '../shell/router.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { VorgangNach } from '../shell/zustand.tsx';

const useStyles = makeStyles({
  liste: { listStyle: 'none', margin: '0.75rem 0 0', padding: 0 },
  // Zeilen wie die Wiedervorlagen (v4 .wvitem): Text links, Aktionen rechts, schmal darunter.
  eintrag: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 1rem',
    padding: '0.75rem 0',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowWrap: 'anywhere',
  },
  text: { flex: '1 1 18rem', minWidth: 0 },
  name: { fontWeight: tokens.fontWeightSemibold },
  // 28 px wie v4 .btn.small.
  knopf: { minHeight: '28px' },
  hinweis: { display: 'block', marginTop: '0.75rem', color: tokens.colorNeutralForeground2 },
  satz: { display: 'block', marginTop: '0.25rem' },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
});

export interface DatenqualitaetScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

export const STATUS_REGION_ID = 'datenqualitaet-status';

export function DatenqualitaetScreen({ apiBaseUrl, accessToken, onReauthenticate }: DatenqualitaetScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [faelle, setFaelle] = useState<readonly DataQualityIssueView[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  // Ladefehler GETRENNT vom Fehler eines Schliessens: ein erfolgreiches Laden
  // raeumt nur den eigenen Fehler ab (Codex-Cloud-Review #274, P2).
  const [ladefehler, setLadefehler] = useState<PlainMessage | undefined>(undefined);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const [fokus, setFokus] = useState(0);
  const laufRef = useRef(0);

  useEffect(() => {
    if (fokus > 0) statusRef.current?.focus();
  }, [fokus]);

  const load = useCallback(async () => {
    const meinLauf = ++laufRef.current;
    setStatus((alt) => (alt === 'geladen' ? alt : 'laedt'));
    try {
      const token = await accessToken();
      if (meinLauf !== laufRef.current) return;
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchOwnIssues({ baseUrl: apiBaseUrl, accessToken: token });
      if (meinLauf !== laufRef.current) return;
      if (result.outcome === 'rejected') {
        setLadefehler(result.message);
        setStatus('abgelehnt');
        return;
      }
      setLadefehler(undefined);
      setFaelle(result.issues);
      setHasMore(result.hasMore);
      setStatus('geladen');
    } catch {
      if (meinLauf !== laufRef.current) return;
      setLadefehler(UNREACHABLE_MESSAGE);
      setStatus('abgelehnt');
    }
  }, [accessToken, apiBaseUrl, onReauthenticate]);

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  const schliessen = async (fall: DataQualityIssueView, art: 'resolve' | 'dismiss'): Promise<void> => {
    setBusy(true);
    setFehler(undefined);
    setBestaetigung(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await closeIssue({ baseUrl: apiBaseUrl, accessToken: token, dataQualityIssueId: fall.dataQualityIssueId, outcome: art });
      if (result.outcome === 'rejected') {
        setFehler(result.message);
      } else {
        setBestaetigung(
          art === 'resolve'
            ? `Geschlossen als behoben: ${fall.partyDisplayName}.`
            : `Geschlossen als gewollt: ${fall.partyDisplayName} behält die Stichworte.`,
        );
      }
      await load();
    } catch {
      setFehler(CLOSE_UNCERTAIN_MESSAGE);
    } finally {
      setBusy(false);
      setFokus((zahl) => zahl + 1);
    }
  };

  return (
    <Card className={styles.card}>
      {/* Ein Satz; die Massenpflege erreicht man ueber die Navigation (Lueckenliste Abschnitt 9). */}
      <Body1>Kontakte, die mehr Stichworte einer Stichwortliste tragen, als sie vorsieht — aus deinen Vergaben und Massenläufen.</Body1>
      <div aria-live="polite" role="status" aria-label="Status: Stichwortkonflikte" id={STATUS_REGION_ID} ref={statusRef} tabIndex={-1} className={own.hinweis}>
        {status === 'laedt' ? <Spinner labelPosition="after" label="Stichwortkonflikte werden geladen" /> : undefined}
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {[fehler, ladefehler].map((meldung) =>
          meldung === undefined ? undefined : (
            // Ein Ladefehler ist ein Fehler; was beim Schliessen offen ist, eine Warnung.
            <MessageBar key={meldung.headline} intent={meldung === ladefehler ? 'error' : 'warning'}>
              <MessageBarBody>
                <MessageBarTitle>{meldung.headline}</MessageBarTitle>
                <div>{meldung.explanation}</div>
                <div>{meldung.nextStep}</div>
              </MessageBarBody>
            </MessageBar>
          ),
        )}
        {status === 'geladen' ? <Caption1>{issuesSummary(faelle.length, hasMore)}</Caption1> : undefined}
      </div>
      <VorgangNach meldungen={[fehler, ladefehler]} />
      {status === 'abgelehnt' && ladefehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}
      {status === 'geladen' && faelle.length > 0 ? (
        <ul className={own.liste} aria-label="Meine Stichwortkonflikte">
          {faelle.map((fall) => {
            const gesperrt = resolveBlockedReason(fall.openTermCount);
            const grundId = `datenqualitaet-grund-${fall.dataQualityIssueId}`;
            return (
            <li key={fall.dataQualityIssueId} className={own.eintrag}>
              <div className={own.text}>
                <AppLink to={partyHref(fall.partyId)} className={own.name}>
                  {fall.partyDisplayName}
                </AppLink>
                <Body1 className={own.satz}>{issueSentence(fall)}</Body1>
                <Caption1 className={own.satz}>{fall.fromBatch ? 'Entstanden bei einem Massenlauf.' : 'Entstanden bei einer Vergabe am Kontakt.'}</Caption1>
                {gesperrt !== undefined ? (
                  <Caption1 id={grundId} className={own.satz}>
                    {gesperrt}
                  </Caption1>
                ) : undefined}
              </div>
              <div className={own.aktionen}>
                <Button
                  appearance="secondary"
                  size="small"
                  className={own.knopf}
                  disabled={busy || gesperrt !== undefined}
                  onClick={() => void schliessen(fall, 'resolve')}
                  aria-label={`Behoben: ${fall.partyDisplayName}`}
                  {...(gesperrt !== undefined ? { 'aria-describedby': grundId } : {})}
                >
                  Behoben
                </Button>
                <Button
                  appearance="secondary"
                  size="small"
                  className={own.knopf}
                  disabled={busy}
                  onClick={() => void schliessen(fall, 'dismiss')}
                  aria-label={`Ist so gewollt: ${fall.partyDisplayName}`}
                >
                  Ist so gewollt
                </Button>
              </div>
            </li>
            );
          })}
        </ul>
      ) : undefined}
    </Card>
  );
}
